import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import { DataTable } from '../components/ui/data-table';
import { Button } from '../components/ui/button';
import { ArrowUpDown, Filter } from 'lucide-react';

const StudentManagement = () => {
  const { authAxios } = useAuth();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClass, setSelectedClass] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentData, setStudentData] = useState({
    name: '',
    email: '',
    phone: '',
    birthDate: '',
    gender: '',
    address: '',
    batch: '',
    category: '',
    campus: '',
    notes: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentStudentId, setCurrentStudentId] = useState(null);
  
  // 인라인 편집을 위한 상태
  const [editingCell, setEditingCell] = useState(null); // {studentId, field}
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);
  const [isComposing, setIsComposing] = useState(false);

  // 필터링을 위한 상태
  const [batchFilter, setBatchFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [campusFilter, setCampusFilter] = useState('');
  
  // 정렬을 위한 상태
  const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

  // 강의 관리 모달을 위한 상태
  const [showClassModal, setShowClassModal] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [allClasses, setAllClasses] = useState([]);
  const [studentClasses, setStudentClasses] = useState({});
  const [loadingClasses, setLoadingClasses] = useState({});
  const [selectedAvailableClasses, setSelectedAvailableClasses] = useState([]);
  const [selectedEnrolledClasses, setSelectedEnrolledClasses] = useState([]);

  // 학생 목록 가져오기
  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      const response = await authAxios.get('/students');
      setStudents(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('교육생 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 강의 목록 가져오기
  const fetchClasses = async () => {
    try {
      const response = await authAxios.get('/classes');
      setClasses(response.data);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  // 컴포넌트 마운트 시 데이터 로딩
  useEffect(() => {
    fetchStudents();
    fetchClasses();
  }, []);

  // 인라인 편집 모드 시작
  const startCellEdit = (student, field) => {
    setEditingCell({ studentId: student._id, field });
    setEditValue(student[field] !== undefined ? student[field].toString() : '');
    
    // 입력 필드에 포커스 주기 위해 다음 틱으로 지연
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
      }
    }, 0);
  };

  // 인라인 편집 취소
  const cancelCellEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  // 인라인 편집 저장
  const saveCellEdit = async () => {
    if (!editingCell) return;
    
    try {
      const { studentId, field } = editingCell;
      const student = students.find(s => s._id === studentId);
      if (!student) return;
      
      // 값 변환 처리 (예: 숫자 필드)
      let value = editValue;
      if (field === 'batch' && value !== '') {
        value = parseInt(value, 10);
        if (isNaN(value)) {
          toast.error('기수는 숫자로 입력해야 합니다.');
          return;
        }
      }
      
      // 학생 전체 데이터 복사 후 필드 업데이트
      const updatedStudentData = { ...student };
      updatedStudentData[field] = value;
      
      // API 호출로 변경 저장 (PATCH 대신 PUT 사용)
      await authAxios.put(`/students/${studentId}`, updatedStudentData);
      
      // 로컬 상태 업데이트
      setStudents(prev => 
        prev.map(s => 
          s._id === studentId ? { ...s, [field]: value } : s
        )
      );
      
      toast.success('변경 사항이 저장되었습니다.');
      cancelCellEdit();
    } catch (err) {
      console.error('인라인 편집 저장 오류:', err);
      toast.error('변경 사항 저장 중 오류가 발생했습니다.');
      cancelCellEdit();
    }
  };

  // 인라인 편집 필드 변경 처리
  const handleEditValueChange = (e) => {
    // 한글 입력 처리를 위해 isComposing 체크 제거
    setEditValue(e.target.value);
  };

  // 키 이벤트 처리
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isComposing) {
      e.preventDefault();
      saveCellEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelCellEdit();
    }
  };
  
  // 한글 입력 완료 이벤트 처리 (추가)
  const handleCompositionEnd = (e) => {
    setIsComposing(false);
    setEditValue(e.target.value);
  };

  // 학생 추가 또는 수정
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        // 학생 정보 수정
        await authAxios.put(`/students/${currentStudentId}`, studentData);
        toast.success('교육생 정보가 수정되었습니다.');
      } else {
        // 새 학생 추가
        await authAxios.post('/students', studentData);
        toast.success('새 교육생이 추가되었습니다.');
      }
      setIsModalOpen(false);
      resetForm();
      fetchStudents(); // 학생 목록 새로고침
    } catch (err) {
      console.error('Error saving student:', err);
      toast.error('교육생 정보 저장 중 오류가 발생했습니다.');
    }
  };

  // 학생 삭제
  const handleDelete = async (studentId) => {
    if (window.confirm('정말로 이 교육생을 삭제하시겠습니까?')) {
      try {
        await authAxios.delete(`/students/${studentId}`);
        toast.success('교육생이 삭제되었습니다.');
        fetchStudents(); // 학생 목록 새로고침
      } catch (err) {
        console.error('Error deleting student:', err);
        toast.error('교육생 삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // 학생 정보 수정 모달 열기
  const handleEdit = (student) => {
    setIsEditing(true);
    setCurrentStudentId(student._id);
    setStudentData({
      name: student.name,
      email: student.email,
      phone: student.phone || '',
      birthDate: student.birthDate ? student.birthDate.split('T')[0] : '',
      gender: student.gender || '',
      address: student.address || '',
      batch: student.batch || '',
      category: student.category || '',
      campus: student.campus || '',
      notes: student.notes || ''
    });
    setIsModalOpen(true);
  };

  // 새 학생 추가 모달 열기
  const handleAdd = () => {
    setIsEditing(false);
    resetForm();
    setIsModalOpen(true);
  };
  
  // 폼 초기화
  const resetForm = () => {
    setStudentData({
      name: '',
      email: '',
      phone: '',
      birthDate: '',
      gender: '',
      address: '',
      batch: '',
      category: '',
      campus: '',
      notes: ''
    });
    setCurrentStudentId(null);
  };

  // 모달 닫기
  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };
  
  // 입력 필드 변경 처리
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setStudentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 학생을 수업에 등록
  const handleEnrollStudent = async (studentId, classId) => {
    try {
      await authAxios.post(`/classes/${classId}/students`, { studentId });
      toast.success('교육생이 강의에 등록되었습니다.');
      fetchStudents(); // 학생 목록 새로고침
    } catch (err) {
      console.error('Error enrolling student:', err);
      toast.error('교육생 등록 중 오류가 발생했습니다.');
    }
  };

  // 학생을 수업에서 제외
  const handleUnenrollStudent = async (studentId, classId) => {
    try {
      await authAxios.delete(`/classes/${classId}/students/${studentId}`);
      toast.success('교육생이 강의에서 제외되었습니다.');
      fetchStudents(); // 학생 목록 새로고침
    } catch (err) {
      console.error('Error unenrolling student:', err);
      toast.error('교육생 제외 중 오류가 발생했습니다.');
    }
      };
      
  // 정렬 처리 함수
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // 정렬된 학생 데이터 계산
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    
    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // null, undefined 처리
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';
      
      // 기수는 숫자로 변환하여 정렬
      if (sortConfig.key === 'batch') {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return sortConfig.direction === 'asc' 
          ? aNum - bNum 
          : bNum - aNum;
      }
      
      // 문자열 비교 (모든 값을 문자열로 변환)
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();
      
      return sortConfig.direction === 'asc'
        ? aStr.localeCompare(bStr, 'ko-KR')
        : bStr.localeCompare(aStr, 'ko-KR');
    });
  };

  // 학생 필터링 (검색어, 선택된 강의, 기수, 교육구분, 교육장)
  const filteredStudents = useMemo(() => {
    let result = students.filter(student => {
      // 검색어 필터링
      const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (student.email && student.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
                           (student.phone && student.phone.includes(searchQuery));
      
      // 선택된 강의 필터링
      const matchesClass = selectedClass === 'all' || 
                           (student.classIds && student.classIds.includes(selectedClass));
      
      // 기수 필터링
      const matchesBatch = !batchFilter || 
                           (student.batch && student.batch.toString() === batchFilter);
      
      // 교육구분 필터링
      const matchesCategory = categoryFilter === 'all' || 
                             (student.category === categoryFilter);
      
      // 교육장 필터링
      const matchesCampus = !campusFilter || 
                           (student.campus && student.campus.toLowerCase().includes(campusFilter.toLowerCase()));
      
      return matchesSearch && matchesClass && matchesBatch && matchesCategory && matchesCampus;
    });

    // 정렬 적용
    return getSortedData(result);
  }, [students, searchQuery, selectedClass, batchFilter, categoryFilter, campusFilter, sortConfig]);

  // 고유한 기수 목록 가져오기
  const uniqueBatches = useMemo(() => {
    const batches = [...new Set(students.map(student => student.batch).filter(Boolean))];
    return batches.sort((a, b) => a - b);
  }, [students]);

  // 고유한 교육장 목록 가져오기
  const uniqueCampuses = useMemo(() => {
    return [...new Set(students.map(student => student.campus).filter(Boolean))];
  }, [students]);

  // 교육생별 강의 목록 가져오기
  const fetchStudentClasses = async (studentId) => {
    try {
      setLoadingClasses(prev => ({ ...prev, [studentId]: true }));
      
      const response = await authAxios.get(`/students/${studentId}/classes`);
      
      setStudentClasses(prev => ({
        ...prev,
        [studentId]: Array.isArray(response.data) ? response.data.map(c => ({
          ...c,
          id: c._id || c.id
        })) : []
      }));
    } catch (error) {
      console.error('학생 강의 목록 조회 오류:', error);
      
      setStudentClasses(prev => ({
        ...prev,
        [studentId]: []
      }));
    } finally {
      setLoadingClasses(prev => ({ ...prev, [studentId]: false }));
        }
  };
        
  // 모든 강의 목록 가져오기
  const fetchAllClasses = async () => {
    try {
      const response = await authAxios.get('/classes');
      
      const classesData = response.data.map(classItem => ({
        ...classItem,
        id: classItem._id || classItem.id
      }));
      setAllClasses(classesData);
    } catch (err) {
      console.error('강의 목록 조회 오류:', err);
      
      // 임시 데이터 사용 (테스트용)
      setAllClasses([
        { id: '1', name: '자바 프로그래밍' },
        { id: '2', name: '웹 개발 기초' },
        { id: '3', name: '데이터베이스 입문' }
      ]);
    }
  };

  // 강의 등록 모달 열기
  const openClassModal = (studentId) => {
    setSelectedStudentId(studentId);
    setSelectedAvailableClasses([]);
    setSelectedEnrolledClasses([]);
    setShowClassModal(true);
  };

  // 강의 등록 모달 닫기
  const closeClassModal = () => {
    setShowClassModal(false);
    setSelectedStudentId(null);
  };

  // 이용 가능한 강의 목록 계산
  const getAvailableClasses = () => {
    if (!selectedStudentId || !studentClasses[selectedStudentId]) return [];

    const enrolledClassIds = studentClasses[selectedStudentId].map(cls => cls.id || cls._id);
    return allClasses.filter(cls => !enrolledClassIds.includes(cls.id));
  };

  // 이용 가능한 강의 선택 토글
  const toggleAvailableClass = (classId) => {
    setSelectedAvailableClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId) 
        : [...prev, classId]
    );
  };

  // 수강 중인 강의 선택 토글
  const toggleEnrolledClass = (classId) => {
    setSelectedEnrolledClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId) 
        : [...prev, classId]
    );
  };

  // 강의 등록하기
  const enrollClasses = async () => {
    if (!selectedStudentId || selectedAvailableClasses.length === 0) return;

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const classId of selectedAvailableClasses) {
        try {
          const formattedClassId = classId?.toString();
          const formattedStudentId = selectedStudentId?.toString();
          
          if (!formattedClassId || !formattedStudentId) {
            errorCount++;
            continue;
          }

          // 학생 강의 등록에 사용할 올바른 엔드포인트 호출
          let response;
          try {
            // 방법 1: 학생 중심 엔드포인트 시도
            response = await authAxios.post(`/students/${formattedStudentId}/enroll`, {
              classId: formattedClassId
            });
          } catch (innerErr) {
            // 방법 2: 강의 등록 직접 엔드포인트 시도
            response = await authAxios.post(`/attendance/enroll`, {
              studentId: formattedStudentId,
              classId: formattedClassId
            });
          }
          
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`강의 등록 오류 (강의 ID: ${classId}):`, err);
        }
      }

      // 결과 메시지
      if (successCount > 0) {
        toast.success(`${successCount}개 강의에 교육생을 등록했습니다.${errorCount > 0 ? `\n${errorCount}개 강의 등록에 실패했습니다.` : ''}`);
      } else if (errorCount > 0) {
        toast.error(`강의 등록에 실패했습니다.`);
      }

      // 강의 목록 갱신
      if (successCount > 0) {
        fetchStudentClasses(selectedStudentId);
      }
      
      setSelectedAvailableClasses([]);
    } catch (err) {
      console.error('등록 과정 중 예상치 못한 오류:', err);
      toast.error('강의 등록 중 오류가 발생했습니다.');
    }
  };

  // 강의 등록 취소하기
  const unenrollClasses = async () => {
    if (!selectedStudentId || selectedEnrolledClasses.length === 0) return;

    try {
      let successCount = 0;
      let errorCount = 0;

      for (const classId of selectedEnrolledClasses) {
        try {
          const formattedClassId = classId?.toString();
          const formattedStudentId = selectedStudentId?.toString();
          
          if (!formattedClassId || !formattedStudentId) {
            errorCount++;
            continue;
          }
          
          // 수강 취소에 사용할 올바른 엔드포인트 호출
          let response;
          try {
            response = await authAxios.post(`/students/${formattedStudentId}/unenroll`, {
              classId: formattedClassId
            });
          } catch (innerErr) {
            response = await authAxios.post(`/attendance/unenroll`, {
              studentId: formattedStudentId,
              classId: formattedClassId
            });
          }
          
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`강의 취소 오류 (강의 ID: ${classId}):`, err);
        }
      }

      // 결과 메시지
      if (successCount > 0) {
        toast.success(`${successCount}개 강의에서 교육생을 제외했습니다.${errorCount > 0 ? `\n${errorCount}개 강의 취소에 실패했습니다.` : ''}`);
      } else if (errorCount > 0) {
        toast.error(`강의 취소에 실패했습니다.`);
      }

      // 강의 목록 갱신
      if (successCount > 0) {
        fetchStudentClasses(selectedStudentId);
      }
      
      setSelectedEnrolledClasses([]);
    } catch (err) {
      console.error('취소 과정 중 예상치 못한 오류:', err);
      toast.error('강의 취소 중 오류가 발생했습니다.');
    }
  };

  // 컬럼 정의 - Shadcn UI 스타일에 맞게 조정
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="px-0 font-medium"
        >
          이름
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const student = row.original;
        
        if (editingCell?.studentId === student._id && editingCell?.field === 'name') {
          return (
            <input
              ref={editInputRef}
              type="text"
              className="w-full px-2 py-1 border rounded-md"
              value={editValue}
              onChange={handleEditValueChange}
              onKeyDown={handleEditKeyDown}
              onBlur={saveCellEdit}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={() => setIsComposing(false)}
            />
          );
        }
        
        return (
          <div 
            className="font-medium cursor-pointer hover:underline"
            onClick={() => startCellEdit(student, 'name')}
          >
            {student.name || '(이름 입력)'}
          </div>
        );
      }
    },
    {
      accessorKey: "phone",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="px-0 font-medium"
        >
          연락처
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      ),
      cell: ({ row }) => {
        const student = row.original;
        
        if (editingCell?.studentId === student._id && editingCell?.field === 'phone') {
          return (
            <input
              ref={editInputRef}
              type="text"
              className="w-full px-2 py-1 border rounded-md"
              value={editValue}
              onChange={handleEditValueChange}
              onKeyDown={handleEditKeyDown}
              onBlur={saveCellEdit}
            />
          );
        }
        
        return (
          <div
            className="cursor-pointer hover:bg-gray-100"
            onClick={() => startCellEdit(student, 'phone')}
          >
            <div className="text-sm text-gray-900">{student.phone || '-'}</div>
            <div className="text-sm text-gray-500">{student.email || '-'}</div>
          </div>
        );
      }
    },
    {
      accessorKey: "batch",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => handleSort('batch')}
          className="px-0 font-medium"
        >
          기수
          <ArrowUpDown className="ml-2 h-4 w-4" />
          {sortConfig.key === 'batch' && (
            <span className="ml-1 text-xs">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
          )}
        </Button>
      ),
      cell: ({ row }) => {
        const student = row.original;
        
        if (editingCell?.studentId === student._id && editingCell?.field === 'batch') {
          return (
            <input
              ref={editInputRef}
              type="number"
              min="1"
              className="w-full px-2 py-1 border rounded-md"
              value={editValue}
              onChange={handleEditValueChange}
              onKeyDown={handleEditKeyDown}
              onBlur={saveCellEdit}
            />
          );
        }
        
        return (
          <div 
            className={`inline-flex text-sm font-bold py-1 px-2 rounded-full cursor-pointer hover:opacity-80 ${student.batch ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
            onClick={() => startCellEdit(student, 'batch')}
          >
            {student.batch ? `${student.batch}기` : '-'}
          </div>
        );
      }
    },
    {
      accessorKey: "category",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => handleSort('category')}
          className="px-0 font-medium"
        >
          교육구분
          <ArrowUpDown className="ml-2 h-4 w-4" />
          {sortConfig.key === 'category' && (
            <span className="ml-1 text-xs">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
          )}
        </Button>
      ),
      cell: ({ row }) => {
        const student = row.original;
        
        if (editingCell?.studentId === student._id && editingCell?.field === 'category') {
          return (
            <select
              ref={editInputRef}
              className="w-full px-2 py-1 border rounded-md"
              value={editValue}
              onChange={handleEditValueChange}
              onKeyDown={handleEditKeyDown}
              onBlur={saveCellEdit}
            >
              <option value="">선택하세요</option>
              <option value="단기">단기</option>
              <option value="중기">중기</option>
              <option value="장기">장기</option>
            </select>
          );
        }
        
        return (
          <div 
            className={`inline-flex text-sm font-bold py-1 px-2 rounded-full cursor-pointer hover:opacity-80
            ${student.category === '단기' ? 'bg-green-100 text-green-800' : 
              student.category === '중기' ? 'bg-yellow-100 text-yellow-800' : 
                student.category === '장기' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'}`}
            onClick={() => startCellEdit(student, 'category')}
          >
            {student.category || '-'}
          </div>
        );
      }
    },
    {
      accessorKey: "campus",
      header: ({ column }) => (
        <Button
          variant="ghost"
          onClick={() => handleSort('campus')}
          className="px-0 font-medium"
        >
          교육장
          <ArrowUpDown className="ml-2 h-4 w-4" />
          {sortConfig.key === 'campus' && (
            <span className="ml-1 text-xs">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
          )}
        </Button>
      ),
      cell: ({ row }) => {
        const student = row.original;
        
        if (editingCell?.studentId === student._id && editingCell?.field === 'campus') {
        return (
            <input
              ref={editInputRef}
              type="text"
              className="w-full px-2 py-1 border rounded-md"
              value={editValue}
              onChange={handleEditValueChange}
              onKeyDown={handleEditKeyDown}
              onBlur={saveCellEdit}
              onCompositionStart={() => setIsComposing(true)}
              onCompositionEnd={handleCompositionEnd}
              autoFocus
            />
          );
        }
        
        return (
          <div 
            className="cursor-pointer hover:underline"
            onClick={() => startCellEdit(student, 'campus')}
          >
            {student.campus || '-'}
          </div>
        );
      }
    },
    {
      id: "actions",
      header: "관리",
      cell: ({ row }) => {
        const student = row.original;
          return (
            <div className="flex space-x-2">
            {/* //   <button
            //   onClick={() => handleEdit(student)}
            //   className="text-indigo-600 hover:text-indigo-900"
            // >
            //   수정
            // </button> */}
            <button
              onClick={() => handleDelete(student._id)}
              className="text-red-600 hover:text-red-900"
            >
              삭제
            </button>
          </div>
        );
      }
    }
  ], [editingCell, editValue, isComposing, sortConfig]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">교육생 관리</h1>
        <p className="mt-1 text-sm text-gray-500">
          교육생을 추가하고 관리하며 강의를 할당할 수 있습니다.
        </p>
      </div>

      {/* 필터 및 검색 섹션 */}
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="w-full sm:w-64">
                <label htmlFor="search" className="sr-only">
                  검색
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <input
                    id="search"
                    name="search"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="이름, 이메일, 전화번호 검색"
                    type="search"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="w-full sm:w-40">
                <label htmlFor="batchFilter" className="sr-only">
                  기수 필터
                </label>
                <select
                  id="batchFilter"
                  name="batchFilter"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={batchFilter}
                  onChange={e => setBatchFilter(e.target.value)}
                >
                  <option value="">모든 기수</option>
                  {uniqueBatches.map(batch => (
                    <option key={batch} value={batch.toString()}>
                      {batch}기
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-40">
                <label htmlFor="categoryFilter" className="sr-only">
                  교육구분 필터
                </label>
                <select
                  id="categoryFilter"
                  name="categoryFilter"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={categoryFilter}
                  onChange={e => setCategoryFilter(e.target.value)}
                >
                  <option value="all">모든 교육구분</option>
                  <option value="단기">단기</option>
                  <option value="중기">중기</option>
                  <option value="장기">장기</option>
                </select>
              </div>

              <div className="w-full sm:w-40">
                <label htmlFor="campusFilter" className="sr-only">
                  교육장 필터
                </label>
                <select
                  id="campusFilter"
                  name="campusFilter"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={campusFilter}
                  onChange={e => setCampusFilter(e.target.value)}
                >
                  <option value="">모든 교육장</option>
                  {uniqueCampuses.map(campus => (
                    <option key={campus} value={campus}>
                      {campus}
                    </option>
                  ))}
                </select>
              </div>

              <div className="w-full sm:w-52">
                <label htmlFor="classFilter" className="sr-only">
                  강의 필터
                </label>
                <select
                  id="classFilter"
                  name="classFilter"
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                  value={selectedClass}
                  onChange={e => setSelectedClass(e.target.value)}
                >
                  <option value="all">모든 강의</option>
                  {classes.map(classItem => (
                    <option key={classItem._id} value={classItem._id}>
                      {classItem.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                onClick={handleAdd}
              >
                교육생 추가
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* 학생 목록 */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
      {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : error ? (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
        ) : filteredStudents.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">검색 결과가 없습니다</h3>
            <p className="mt-1 text-sm text-gray-500">다른 검색어를 입력하거나 필터를 확인해주세요.</p>
        </div>
      ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    번호
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    이름
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    연락처
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    기수
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    교육구분
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    교육장
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    관리
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student, index) => (
                  <tr key={student._id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{index + 1}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        
                        <div className="">
                          {editingCell?.studentId === student._id && editingCell?.field === 'name' ? (
                            <input
                              ref={editInputRef}
                              type="text"
                              className="w-full px-2 py-1 border rounded-md text-sm font-medium text-gray-900"
                              value={editValue}
                              onChange={handleEditValueChange}
                              onKeyDown={handleEditKeyDown}
                              onBlur={saveCellEdit}
                              onCompositionStart={() => setIsComposing(true)}
                              onCompositionEnd={handleCompositionEnd}
                              autoFocus
                            />
                          ) : (
                            <div 
                              className="text-sm font-medium text-gray-900 cursor-pointer hover:underline"
                              onClick={() => startCellEdit(student, 'name')}
                            >
                              {student.name || '(이름 입력)'}
                            </div>
                          )}
                          <div className="text-sm text-gray-500">{student.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCell?.studentId === student._id && editingCell?.field === 'phone' ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          className="w-full px-2 py-1 border rounded-md text-sm text-gray-900"
                          value={editValue}
                          onChange={handleEditValueChange}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveCellEdit}
                        />
                      ) : (
                        <div 
                          className="text-sm text-gray-900 cursor-pointer hover:underline"
                          onClick={() => startCellEdit(student, 'phone')}
                        >
                          {student.phone || '-'}
            </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCell?.studentId === student._id && editingCell?.field === 'batch' ? (
                        <input
                          ref={editInputRef}
                          type="number"
                          min="1"
                          className="w-full px-2 py-1 border rounded-md text-sm"
                          value={editValue}
                          onChange={handleEditValueChange}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveCellEdit}
                        />
                      ) : (
                        <div 
                          className={`inline-flex text-sm font-bold py-1 px-2 rounded-full cursor-pointer hover:opacity-80 ${student.batch ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
                          onClick={() => startCellEdit(student, 'batch')}
                        >
                          {student.batch ? `${student.batch}기` : '-'}
              </div>
            )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCell?.studentId === student._id && editingCell?.field === 'category' ? (
                        <select
                          ref={editInputRef}
                          className="w-full px-2 py-1 border rounded-md text-sm"
                          value={editValue}
                          onChange={handleEditValueChange}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveCellEdit}
                        >
                          <option value="">선택하세요</option>
                          <option value="단기">단기</option>
                          <option value="중기">중기</option>
                          <option value="장기">장기</option>
                        </select>
                      ) : (
                        <div 
                          className={`inline-flex text-sm font-bold py-1 px-2 rounded-full cursor-pointer hover:opacity-80
                            ${student.category === '단기' ? 'bg-green-100 text-green-800' : 
                            student.category === '중기' ? 'bg-yellow-100 text-yellow-800' : 
                            student.category === '장기' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'}`}
                          onClick={() => startCellEdit(student, 'category')}
                        >
                          {student.category || '-'}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingCell?.studentId === student._id && editingCell?.field === 'campus' ? (
                        <input
                          ref={editInputRef}
                          type="text"
                          className="w-full px-2 py-1 border rounded-md text-sm text-gray-900"
                          value={editValue}
                          onChange={handleEditValueChange}
                          onKeyDown={handleEditKeyDown}
                          onBlur={saveCellEdit}
                          onCompositionStart={() => setIsComposing(true)}
                          onCompositionEnd={handleCompositionEnd}
                          autoFocus
                        />
                      ) : (
                        <div 
                          className="text-sm text-gray-900 cursor-pointer hover:underline"
                          onClick={() => startCellEdit(student, 'campus')}
                        >
                          {student.campus || '-'}
                </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                  <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={() => handleEdit(student)}
                        >
                          수정
                        </button>
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-900"
                          onClick={() => handleDelete(student._id)}
                        >
                          삭제
                  </button>
                </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
              </div>
            )}
              </div>
              
      {/* 학생 추가/수정 모달 */}
      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        {isEditing ? '교육생 정보 수정' : '새 교육생 추가'}
                      </h3>
                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                              이름 *
                            </label>
                        <input
                              type="text"
                              name="name"
                              id="name"
                              required
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.name}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                              이메일
                        </label>
                            <input
                              type="email"
                              name="email"
                              id="email"
                              
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.email}
                              onChange={handleInputChange}
                            />
                </div>
                </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                              전화번호 * 
                            </label>
                            <input
                              type="text"
                              name="phone"
                              id="phone"
                              required
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.phone}
                              onChange={handleInputChange}
                            />
              </div>
              <div>
                            <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700">
                              생년월일
                            </label>
                        <input
                              type="date"
                              name="birthDate"
                              id="birthDate"
                              
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.birthDate}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                              성별
                            </label>
                            <select
                              name="gender"
                              id="gender"
                              
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.gender}
                              onChange={handleInputChange}
                            >
                              <option value="">선택하세요</option>
                              <option value="남성">남성</option>
                              <option value="여성">여성</option>
                            </select>
                          </div>
                          <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                              주소
                        </label>
                            <input
                              type="text"
                              name="address"
                              id="address"
                              
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.address}
                              onChange={handleInputChange}
                            />
                </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          <div>
                            <label htmlFor="batch" className="block text-sm font-medium text-gray-700">
                              기수
                            </label>
                            <input
                              type="number"
                              name="batch"
                              id="batch"
                              min="1"
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.batch}
                              onChange={handleInputChange}
                            />
                          </div>
                          <div>
                            <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                              교육구분
                            </label>
                            <select
                              name="category"
                              id="category"
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.category}
                              onChange={handleInputChange}
                            >
                              <option value="">선택하세요</option>
                              <option value="단기">단기</option>
                              <option value="중기">중기</option>
                              <option value="장기">장기</option>
                            </select>
                </div>
                          <div>
                            <label htmlFor="campus" className="block text-sm font-medium text-gray-700">
                              교육장
                            </label>
                            <input
                              type="text"
                              name="campus"
                              id="campus"
                              className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                              value={studentData.campus}
                              onChange={handleInputChange}
                            />
              </div>
            </div>
                        <div>
                          <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                            비고
                          </label>
                          <textarea
                            name="notes"
                            id="notes"
                            rows="3"
                            className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                            value={studentData.notes}
                            onChange={handleInputChange}
                          ></textarea>
              </div>
            </div>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    {isEditing ? '수정' : '추가'}
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleCloseModal}
                  >
                    취소
              </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentManagement; 